import {
  Latch,
  type FighterInputSource,
  type FighterIntent,
  type GroundProjector,
} from './fighterInput';

const FORWARD = new Set(['KeyW', 'ArrowUp']);
const BACK = new Set(['KeyS', 'ArrowDown']);
const LEFT = new Set(['KeyA', 'ArrowLeft']);
const RIGHT = new Set(['KeyD', 'ArrowRight']);
const DASH = new Set(['Space']);
// Not Enter: it also pressed whatever button had the focus, the HUD's fold handle say, on every
// attack (client-input:bugs#8). The HUD names F and the left mouse button.
const ATTACK_KEYS = new Set(['KeyF', 'KeyE']);

/**
 * Mouse and keyboard under the fixed camera: WASD or the arrows move the fighter the way they
 * point **on the screen** and it turns that way, the cursor is where an attack goes, space dashes,
 * and the left mouse button or F attacks.
 *
 * There is no pointer lock any more. The camera does not turn with the mouse, so taking the
 * cursor away would only hide the thing the player aims with.
 */
export function createDesktopFighterInput(
  canvas: HTMLCanvasElement,
  groundAt: GroundProjector,
): FighterInputSource {
  const keys = new Set<string>();
  const dash = new Latch();
  const attack = new Latch();
  let pointer: { x: number; y: number } | null = null;

  const onKey = (down: boolean) => (e: KeyboardEvent) => {
    // A key pressed in a form field is the field's. One let go there still ends its press: a W
    // held while the volume slider was clicked kept the fighter walking (client-input:quality#4).
    if (
      down &&
      e.target instanceof HTMLElement &&
      /^(INPUT|SELECT|TEXTAREA)$/.test(e.target.tagName)
    )
      return;
    if (down) keys.add(e.code);
    else keys.delete(e.code);
    if (DASH.has(e.code)) dash.set(down);
    if (ATTACK_KEYS.has(e.code)) attack.set(down);
    if (DASH.has(e.code) || FORWARD.has(e.code) || BACK.has(e.code)) e.preventDefault();
  };
  const onKeyDown = onKey(true);
  const onKeyUp = onKey(false);

  const onMouseDown = (e: MouseEvent) => {
    // A click in the arena is back in the fight: a button clicked a moment ago keeps the focus
    // otherwise, and the fight's keys reach it too (client-input:bugs#8).
    const focused = document.activeElement;
    if (focused instanceof HTMLElement && focused !== document.body) focused.blur();
    if (e.button === 0) attack.set(true);
  };
  const onMouseUp = (e: MouseEvent) => {
    if (e.button === 0) attack.set(false);
  };
  const onMouseMove = (e: MouseEvent) => {
    pointer = { x: e.clientX, y: e.clientY };
  };
  const onLeave = () => {
    pointer = null;
  };
  const onBlur = () => {
    keys.clear();
    dash.reset();
    attack.reset();
  };
  // A tab put away can miss its keys' release where a window's blur is not sent.
  const onVisibility = () => {
    if (document.hidden) onBlur();
  };
  const onContextMenu = (e: Event) => e.preventDefault();

  window.addEventListener('keydown', onKeyDown);
  window.addEventListener('keyup', onKeyUp);
  window.addEventListener('blur', onBlur);
  document.addEventListener('visibilitychange', onVisibility);
  canvas.addEventListener('mousedown', onMouseDown);
  canvas.addEventListener('contextmenu', onContextMenu);
  canvas.addEventListener('mouseleave', onLeave);
  document.addEventListener('mouseup', onMouseUp);
  document.addEventListener('mousemove', onMouseMove);

  const axis = (positive: Set<string>, negative: Set<string>) =>
    ([...keys].some((k) => positive.has(k)) ? 1 : 0) -
    ([...keys].some((k) => negative.has(k)) ? 1 : 0);

  return {
    update() {},
    next(): FighterIntent {
      // The camera looks along +z square to the grid, so "up the screen" is +z in the world and
      // the keys need no rotating. If the camera ever turns, this is the one place to turn them.
      const moveX = axis(RIGHT, LEFT);
      const moveZ = axis(FORWARD, BACK);
      const length = Math.hypot(moveX, moveZ);
      return {
        moveX: length > 1 ? moveX / length : moveX,
        moveZ: length > 1 ? moveZ / length : moveZ,
        aimAt: pointer ? groundAt(pointer.x, pointer.y) : null,
        aimDir: null,
        dash: dash.take(),
        attack: attack.take(),
      };
    },
    dispose() {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
      document.removeEventListener('visibilitychange', onVisibility);
      canvas.removeEventListener('mousedown', onMouseDown);
      canvas.removeEventListener('contextmenu', onContextMenu);
      canvas.removeEventListener('mouseleave', onLeave);
      document.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('mousemove', onMouseMove);
    },
  };
}
